/**
 * React Query hooks for analytics data
 *
 * These hooks wrap the analytics API with React Query caching.
 * Currently NOT used by the main Analytics page (which uses Grafana),
 * but preserved for future use cases:
 *
 * - Admin dashboard
 * - Mobile views
 * - Embedded widgets on other pages
 *
 * See: webapp/frontend/src/components/charts/index.ts for full explanation.
 */
import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../api/analytics';
import type {
  AllocationGroupBy,
  PerformanceSortBy,
} from '../types/analytics';

const ANALYTICS_QUERY_KEYS = {
  summary: (portfolioId?: string, tagIds?: string[]) =>
    ['analytics', 'summary', portfolioId, tagIds] as const,
  portfolioHistory: (days: number, portfolioId?: string, tagIds?: string[]) =>
    ['analytics', 'portfolio-history', days, portfolioId, tagIds] as const,
  allocation: (groupBy: AllocationGroupBy, portfolioId?: string, tagIds?: string[]) =>
    ['analytics', 'allocation', groupBy, portfolioId, tagIds] as const,
  performance: (sortBy: PerformanceSortBy, limit: number, portfolioId?: string, tagIds?: string[]) =>
    ['analytics', 'performance', sortBy, limit, portfolioId, tagIds] as const,
};

export interface UsePortfolioHistoryOptions {
  days?: number;
  portfolioId?: string;
  tagIds?: string[];
  enabled?: boolean;
}

export function usePortfolioHistory(options: UsePortfolioHistoryOptions = {}) {
  const { days = 30, portfolioId, tagIds, enabled = true } = options;

  return useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.portfolioHistory(days, portfolioId, tagIds),
    queryFn: () => analyticsApi.getPortfolioHistory({ days, portfolioId, tagIds }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export interface UseAllocationOptions {
  groupBy?: AllocationGroupBy;
  portfolioId?: string;
  tagIds?: string[];
  enabled?: boolean;
}

export function useAllocation(options: UseAllocationOptions = {}) {
  const { groupBy = 'account', portfolioId, tagIds, enabled = true } = options;

  return useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.allocation(groupBy, portfolioId, tagIds),
    queryFn: () => analyticsApi.getAllocation({ groupBy, portfolioId, tagIds }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export interface UseHoldingsPerformanceOptions {
  limit?: number;
  sortBy?: PerformanceSortBy;
  portfolioId?: string;
  tagIds?: string[];
  enabled?: boolean;
}

export function useHoldingsPerformance(options: UseHoldingsPerformanceOptions = {}) {
  const { limit = 10, sortBy = 'gain_pct', portfolioId, tagIds, enabled = true } = options;

  return useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.performance(sortBy, limit, portfolioId, tagIds),
    queryFn: () => analyticsApi.getHoldingsPerformance({ limit, sortBy, portfolioId, tagIds }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export interface UsePortfolioSummaryOptions {
  portfolioId?: string;
  tagIds?: string[];
  enabled?: boolean;
}

export function usePortfolioSummary(options: UsePortfolioSummaryOptions = {}) {
  const { portfolioId, tagIds, enabled = true } = options;

  return useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.summary(portfolioId, tagIds),
    queryFn: () => analyticsApi.getSummary(portfolioId, tagIds),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
